import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { isRealAssignment } from '../utils/personalSpace';
import { useToastOrNoop } from './ToastContext';
import { apiGet, apiPost, apiPut, apiThrow } from '../utils/api';

// 🔁 REMEMBER THE LAST REAL DEPARTMENT ON THE SERVER (owner 2026-10-04: "logging
// out and back in should keep you on the last department"). Logout clears the
// browser's copy, so the next login — on any device — falls back to this. Fire
// and forget: a failed save costs only the convenience, never the switch. A
// personal space is never sent (the server would ignore it anyway).
function rememberLastDepartment(dept) {
  if (!dept || !isRealAssignment(dept)) return;
  try {
    Promise.resolve(apiPut('/api/roles/my-last-department', { department_id: dept.department_id })).catch(() => {});
  } catch (_) { /* never let the convenience break a switch */ }
}

const DepartmentContext = createContext(null);

const STORAGE_KEY = 'sfx_active_department_id';

/**
 * The department id of a personal space the user EXPLICITLY chose this session.
 *
 * 🔑 **`sessionStorage`, NOT `localStorage`, AND THE STORAGE CHOICE *IS* THE
 * FEATURE.** Product owner, 2026-08-11: *"every login will default to the
 * default department and NOT myspace unless all they have is myspace"* — while
 * also requiring that an accepted switch into My Space is not undone by the
 * next page load. Those two are only compatible if the acceptance is scoped to
 * the SESSION: `sessionStorage` survives a refresh (so the confirmed choice
 * holds, and the warning dialog is not lying about what it just did) and dies
 * with the tab (so a fresh visit lands in the real department again).
 * `localStorage` would make My Space sticky forever, which is the defect the
 * landing rule exists to fix, re-created one dialog later.
 *
 * ⚠️ It is ALSO cleared on logout (`AuthContext.logout`), because a session in
 * the browser's sense outlives a session in the product's sense — logging out
 * and back in as somebody else in the same tab must not inherit the previous
 * user's acceptance.
 */
const SESSION_PERSONAL_ACK_KEY = 'sfx_myspace_session_choice';

/** Read a key without assuming the storage exists (Safari private mode throws). */
const safeRead = (store, key) => {
  try { return store.getItem(key); } catch { return null; }
};
const safeWrite = (store, key, value) => {
  try { store.setItem(key, value); } catch { /* storage unavailable — non-fatal */ }
};
const safeRemove = (store, key) => {
  try { store.removeItem(key); } catch { /* storage unavailable — non-fatal */ }
};

/**
 * The answer for a department the caller holds no assignment in, and for a
 * department id that is missing or not a positive integer.
 *
 * Frozen and module-level so it is referentially stable: `capabilitiesFor`
 * is memoised on `assignments`, and returning a fresh `{}` each call would
 * make every consumer's `useMemo`/`useEffect` on the result re-run forever.
 *
 * ABSENT MEANS FALSE, and it must: `capabilitiesFor(undefined).canX` is
 * `undefined`, which is falsy, so a caller who forgets the id is REFUSED.
 * That mirrors `departmentAuthority`'s EMPTY_AUTHORITY
 * (server/helpers/departmentAuthority.js) — a missing department id is a
 * refusal, never a bypass.
 */
const NO_CAPABILITIES = Object.freeze({});

/**
 * THE ONE RULE for which department a session lands in.
 *
 * Product owner, 2026-08-11: *"We need to make sure when Jakes crew signup/ log
 * in they always end up in the production NOT myspace unless they signup before
 * being added. Once added the default becomes that department/production"*, and
 * earlier the same day: *"Myspace is default only if they have no other
 * department/production."*
 *
 * Precedence, in order:
 *   1. a stored choice that is a REAL department the caller still holds — wins;
 *   2. otherwise the FIRST REAL department, in the server's order;
 *   3. otherwise — genuinely nowhere real to be — the stored personal space,
 *      else the first assignment.
 *
 * 🚨 A STORED **PERSONAL** CHOICE IS DELIBERATELY OVERRIDDEN AT (2), AND THAT
 * IS THE OWNER'S THIRD CASE RATHER THAN AN OVERSIGHT. *"Once added the default
 * becomes that department/production"* — a crew member who signed up before
 * anyone seated them correctly landed in My Space and had that id stored. If a
 * stored id always won, being added to a department a week later would change
 * nothing: they would open the app, see My Space forever, and report it broken.
 * ⚠️ The cost, stated rather than discovered: someone who deliberately switched
 * into My Space last session is moved out of it on their next load. Judged
 * correct — My Space is a personal workspace, not somewhere people work daily,
 * and `switchDepartment` puts them back in one click for the rest of the
 * session.
 *
 * 🔑 (3) IS WHY THIS SORTS RATHER THAN FILTERS. A user whose only space is
 * personal must still land there — that is the *"unless they signup before
 * being added"* case, and dropping personal assignments would lock them out of
 * the only workspace they have.
 *
 * Exported for its own unit tests: the branch that matters most (a stored
 * personal id being overridden) is invisible in a rendered DOM, because both
 * the old and new behaviour render *a* department.
 *
 * @param {Array} depts   assignments, in the server's order (personal LAST —
 *                        `GET /api/roles/my-assignments` sorts
 *                        `COALESCE(p.is_personal, 0)` first)
 * @param {string|number|null} savedId  localStorage.sfx_active_department_id
 * @returns {object|null} the assignment to make active
 */
export function chooseActiveDepartment(depts, savedId, personalAckId) {
  if (!Array.isArray(depts) || depts.length === 0) return null;

  // 🔻 THE "All Productions" SENTINEL SHORT-CIRCUIT WAS REMOVED HERE ON
  // 2026-08-31 with the rest of the mode. A stored `all-my-departments`
  // now names no department, so `held()` below cannot match it and it
  // falls through the ranked rule to "first real department" — which is
  // exactly the right landing for a browser still carrying the value from
  // before the deploy, and the reason no migration of the stored key is
  // needed. **Never re-add a value-specific branch here**: this function's
  // ranked rule is the ONE landing rule (root CLAUDE.md, localStorage
  // keys), and a second, earlier answer is how it stops being one.

  // String-compare: storage is TEXT and department_id is a NUMBER, so a bare
  // === empties the match for everyone while looking like it works — the
  // `sfx_user_id` trap recorded in docs/ROLES.md §A.3, same shape.
  //
  // A stale id — one naming a department the caller no longer holds — simply
  // does not match, so it falls through to the rule. Fails safe, never open:
  // a match can only ever be one of the caller's OWN assignments.
  const held = (id) => (id != null && id !== ''
    ? depts.find((d) => String(d.department_id) === String(id))
    : null);

  const saved = held(savedId);
  if (saved && isRealAssignment(saved)) return saved;

  // 🆕 AN EXPLICITLY CONFIRMED PERSONAL SPACE HOLDS FOR THE REST OF THE
  // SESSION. Product owner, 2026-08-11, after testing the landing fix on
  // staging: *"if the user chooses Myspace give them a pop up warnign to tell
  // them they are leaving their department and how to get back."*
  //
  // 🔑 THIS CLAUSE IS WHAT MAKES THE DIALOG HONEST, and without it the dialog
  // is worse than no dialog. Before it, a refresh evicted the user from My
  // Space — so a warning saying "you are leaving your department" would have
  // described something that then silently did not happen, and the user would
  // have been bounced out with no explanation moments after accepting. An
  // accepted choice that is immediately overridden trains people to distrust
  // every confirmation in the app.
  //
  // ⚠️ It sits AFTER the stored-real branch on purpose: switching back to a
  // real department clears the ack (`switchDepartment`), so the two can never
  // both be live — but if a future change ever leaves a stale ack behind, the
  // REAL department must still win. Fail towards somewhere the user can work.
  const acked = held(personalAckId);
  if (acked && !isRealAssignment(acked)) return acked;

  const firstReal = depts.find(isRealAssignment);
  if (firstReal) return firstReal;

  return saved || depts[0];
}

export function DepartmentProvider({ children }) {
  const [assignments, setAssignments] = useState([]);
  const [activeDepartment, setActiveDepartment] = useState(null);
  const [activeProduction, setActiveProduction] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBuyer, setIsBuyer] = useState(false);
  const [canViewBudget, setCanViewBudget] = useState(false);
  const [canManageBudget, setCanManageBudget] = useState(false);
  // Did this caller CREATE a real production label? Straight from
  // GET /api/roles/my-assignments — the server's own answer to the CREATOR
  // disjunct of PUT /api/productions/:id, never derived here.
  //
  // 🔑 IT DECIDES WHETHER A LINK IS DRAWN AND A DOOR OPENS, AND NOTHING ELSE.
  // See `holdsDepartmentSettingsSomewhere` below and the comment on BuyerRoute
  // in App.js: it grants no authority, and every request behind it is still
  // resolved per department by the server.
  const [ownsProductionLabel, setOwnsProductionLabel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resolved, setResolved] = useState(false); // true once we have a definitive answer (not aborted)
  // 🚨 "WE COULD NOT ASK" IS NOT "YOU BELONG NOWHERE" — 2026-08-22.
  //
  // `assignments: []` is the shape a genuinely unseated user has AND the shape
  // a failed `GET /api/roles/my-assignments` leaves behind, and until this flag
  // existed nothing downstream could tell them apart. Both rendered the same
  // confident claims: `ProductionDepartmentSwitcher` says *"No department
  // assigned"*, `Approvals.js` says *"You are not currently assigned to a
  // department… Ask an administrator to add you to one."*, and every
  // capability-gated control in the app disappears because `capabilities`
  // derives from a null `activeDepartment`. A user whose wifi dropped was
  // therefore told, in the app's own voice, that their seat had been taken
  // away — and sent to an administrator to be re-seated in a department they
  // are already in.
  //
  // 🔑 Consumers must gate the "you are seated nowhere" copy on `!assignmentsError`,
  // exactly as `Approvals.js` already gates its "All caught up!" state on
  // `!error`. This is a load-failure signal, never an authority signal: it says
  // the question was not answered, and nothing about the answer.
  const [assignmentsError, setAssignmentsError] = useState(false);

  // 🆕 PENDING DEPARTMENT INVITES (join-a-second-department, P5, 2026-09-23).
  //
  // Owner ruling R16: *"the invite should never disapear … the new department
  // should always show in their department/production drop down list so they
  // can click onit any time and they get the pop up asking them if they want to
  // join? … they shouldnt be allowed in until they click accept/join/agree"*.
  //
  // 🚨 A SEPARATE ARRAY, AND IT MUST NEVER BE MERGED INTO `assignments`. A
  // pending invite is NOT a seat (`docs/ROLES.md` §C.2). `chooseActiveDepartment`,
  // `switchDepartment` and `capabilitiesFor` all read `assignments` and nothing
  // else, so keeping invites out of it is what makes "a pending department can
  // never become active" true BY CONSTRUCTION rather than by a check someone
  // could forget. A stored `sfx_active_department_id` naming a pending
  // department therefore simply fails to match and falls through the landing
  // rule. Pinned by `DepartmentContext.pendingInvites.test.js`.
  //
  // ⚠️ A failed load is NOT `assignmentsError`: that flag drives the switcher's
  // "Couldn't load your departments" copy, which would be a false claim about
  // the seats the user DOES hold. A failed invite load empties the list (never
  // keeps a previous account's invites after an account switch) and says
  // nothing else.
  const [pendingInvites, setPendingInvites] = useState([]);
  // Latest-wins: a slow response for a previous account (or an older refresh)
  // must never overwrite a newer one.
  const pendingInvitesSeqRef = useRef(0);
  // TRUE once the first invite load has answered (success or failure). The
  // bell asks for a refresh on an UNKNOWN invite id only after this, so a page
  // load costs ONE `GET /api/department-invites/mine` — the context's own —
  // rather than one more per bell instance whose first poll lands before it
  // (measured: three per load before this flag).
  const [pendingInvitesLoaded, setPendingInvitesLoaded] = useState(false);

  // 🆕 TOAST ON EVERY SWITCH (Task 1, 2026-08-31 plan) — `useToastOrNoop`,
  // NEVER `useToast`. `DepartmentProvider` is mounted bare (no
  // `ToastProvider` ancestor) by several existing test harnesses
  // (`DepartmentContext.capabilities.test.js` and siblings), so the throwing
  // `useToast()` would break them at mount, independent of anything
  // `switchDepartment` does. In the real app `App.js` nests `ToastProvider`
  // around `DepartmentProvider`, so this resolves to the real toast there.
  const toast = useToastOrNoop();

  // `quiet` (join-a-second-department, P5): refetch WITHOUT flipping `loading`.
  // Used by `acceptInvite` and the `sfx-role-change` refresh — `loading` makes
  // the switcher render its skeleton (unmounting the Join dialog awaiting this
  // refetch) and BuyerRoute render null (remounting the page). Only the first
  // load omits it.
  const fetchAssignments = useCallback(async (signal, { quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      // Identity comes from session cookie (credentials: include), not headers
      const res = await fetch('/api/roles/my-assignments', {
        credentials: 'include',
        signal
      });
      if (!res.ok) throw new Error('Failed to fetch assignments');
      const data = await res.json();

      // Cleared only once a real answer has landed. A retry (`sfx-role-change`)
      // that succeeds must clear a previous failure, or the honest-error copy
      // outlives the failure it described.
      setAssignmentsError(false);
      setIsAdmin(data.isAdmin);
      setIsBuyer(data.isBuyer || false);
      setCanViewBudget(data.canViewBudget || false);
      setCanManageBudget(data.canManageBudget || false);
      setOwnsProductionLabel(data.ownsProductionLabel || false);
      setAssignments(data.assignments || []);

      const depts = data.assignments || [];

      if (data.isAdmin) {
        // Admins hold no department assignments at all, so there is nothing to
        // choose between — `isAdmin` is what carries them (CLAUDE.md, Role
        // Checking). Unchanged by the landing rule, and it must stay that way:
        // this branch returns before `chooseActiveDepartment` is ever consulted.
        setActiveDepartment(null);
      } else {
        // ONE RULE, three branches collapsed into it. This used to be a
        // `length === 1` / `length > 1` split where the multi case restored ANY
        // stored id — including a personal one — which is the defect: a crew
        // member seated in a department after they had already signed up went
        // on landing in My Space forever. See `chooseActiveDepartment` above.
        // This browser's own choice first (a live session is never yanked
        // elsewhere); after a logout, or on a new device, the server's
        // remembered last department (2026-10-04). Either is honoured only if
        // it names one of `depts` — `chooseActiveDepartment` ignores the rest.
        // 🪤 A session that CONFIRMED My Space keeps it (the ack), so the server
        // value is consulted only when there is no ack and no real stored id.
        const storedId = safeRead(localStorage, STORAGE_KEY);
        const ackId = safeRead(sessionStorage, SESSION_PERSONAL_ACK_KEY);
        const storedIsReal = depts.some((d) => String(d.department_id) === String(storedId) && isRealAssignment(d));
        const useServer = !storedIsReal && !ackId && data.lastDepartmentId != null;
        const chosen = chooseActiveDepartment(
          depts,
          useServer ? String(data.lastDepartmentId) : storedId,
          ackId
        );
        setActiveDepartment(chosen);

        // 🔑 WRITE THE RESOLVED CHOICE BACK, ALWAYS — not only when defaulting,
        // which is what the old code did. `utils/api.js` sends this exact value
        // as `x-department-id` on every request, so leaving a stale/overridden
        // id in storage would have the SERVER resolving one department while
        // the UI displays another: two answers to one question, and the writes
        // would land where the switcher says they are not.
        if (chosen) {
          safeWrite(localStorage, STORAGE_KEY, String(chosen.department_id));
        } else {
          safeRemove(localStorage, STORAGE_KEY);
        }
      }
      setResolved(true);
    } catch (err) {
      // An ABORTED fetch is not a failure — the provider unmounted or the user
      // switched account mid-flight — so it alone returns without a verdict.
      if (err.name === 'AbortError') return;
      // 🚨 A REJECTED FETCH NOW TAKES THE SAME PATH AS A NON-OK RESPONSE, AND
      // THE OLD EARLY-RETURN WAS WORSE THAN THE BUG IT GUARDED — 2026-08-22.
      //
      // This line used to read `err.name === 'AbortError' || (err instanceof
      // TypeError && err.message === 'Failed to fetch')`, on the stated grounds
      // that a *navigation*-aborted fetch rejects that way. It does — but so
      // does every offline, DNS-failed, CORS-refused and server-unreachable
      // request, which is not a rarity, and the early return left `resolved`
      // FALSE while the `finally` still cleared `loading`. `loading: loading ||
      // !resolved` is therefore stuck TRUE for the life of the page: the
      // switcher renders its skeleton and `Approvals.js` renders a spinner,
      // FOREVER, with no error and no retry. So the two failure paths did not
      // in fact land in the same place — one lied ("No department assigned"),
      // the other hung. Both are now one path with an honest flag on it.
      //
      // The navigation case it was protecting is moot: the document is being
      // torn down, so nothing it sets is ever painted.
      console.error('DepartmentContext: failed to fetch assignments', err);
      setAssignmentsError(true);
      setAssignments([]);
      setActiveDepartment(null);
      setIsAdmin(false);
      setIsBuyer(false);
      setOwnsProductionLabel(false);
      setResolved(true);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load the caller's pending department invites.
   *
   * 🔀 RESOLVES `{ ok: true, invites }` OR `{ ok: false }` (review R1,
   * 2026-09-23) — it used to resolve the bare list, and `[]` on every failure,
   * so a caller could not tell "could not ask" from "asked, and it is absent".
   * The bell read a failed fetch as "the invite is gone", marked a LIVE invite's
   * notification read and said "no longer open" — defeating "bell keeps it" on
   * the phone route. Only `{ ok: true }` with the id absent may mean "gone".
   * The CONTEXT state is unchanged by this: a failure still empties
   * `pendingInvites` and never sets `assignmentsError`.
   *
   * Refreshed on mount, on `sfx-role-change`, after an accept, and when the
   * bell sees a `department_invite` notification it does not recognise —
   * deliberately NO interval: the bell's own poll is the only timer, and a
   * second one would be a second background poll in the activity log.
   */
  const refreshPendingInvites = useCallback(async () => {
    const seq = ++pendingInvitesSeqRef.current;
    try {
      const res = await apiGet('/api/department-invites/mine');
      if (!res.ok) await apiThrow(res);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      if (seq === pendingInvitesSeqRef.current) setPendingInvites(list);
      return { ok: true, invites: list };
    } catch {
      // Never `setAssignmentsError` — see the state declaration.
      if (seq === pendingInvitesSeqRef.current) setPendingInvites([]);
      return { ok: false };
    } finally {
      setPendingInvitesLoaded(true);
    }
  }, []);

  /**
   * Accept a pending invite and land in its department.
   *
   * 🔑 NO NEW `setActiveDepartment` CALL (the count is pinned at 4 by
   * `activeDepartmentKeys.manifest.test.js` §3.1). The landing goes through
   * the ONE landing rule instead: write the returned id to storage, then
   * refetch the assignments — the new seat is now IN `assignments`, so
   * `chooseActiveDepartment`'s "a stored REAL department wins" branch picks
   * it. The personal-space session ack is spent exactly as `switchDepartment`
   * spends it on a move to somewhere real.
   *
   * The refetch is `quiet` so the switcher does not drop to its skeleton and
   * unmount the Join dialog that is awaiting this promise.
   *
   * Throws the `apiThrow` Error (carrying `.status` / `.code`) on a refusal,
   * having written NOTHING — the caller shows the server's message.
   */
  //
  // 🔀 CONSENT TO WHAT WAS SHOWN (review R3, 2026-09-23): the body carries
  // `{ role }` — the role the Join prompt DISPLAYED. The server refuses with
  // 409 `invite_changed` when the invite's role no longer matches (P3's next
  // round; until then it ignores the body), so nobody joins at a role they
  // were never shown.
  const acceptInvite = useCallback(async (inviteId, shownRole) => {
    const res = await apiPost(
      `/api/department-invites/${encodeURIComponent(inviteId)}/accept`,
      { role: shownRole }
    );
    if (!res.ok) await apiThrow(res);
    const data = await res.json();
    if (data && data.department_id != null) {
      safeWrite(localStorage, STORAGE_KEY, String(data.department_id));
      safeRemove(sessionStorage, SESSION_PERSONAL_ACK_KEY);
      // A Join lands you there, so it is also your "last department".
      try { Promise.resolve(apiPut('/api/roles/my-last-department', { department_id: data.department_id })).catch(() => {}); } catch (_) { /* convenience only */ }
    }
    await fetchAssignments(undefined, { quiet: true });
    await refreshPendingInvites();
    return data;
  }, [fetchAssignments, refreshPendingInvites]);

  // Fetch on mount and re-fetch when user switches account
  useEffect(() => {
    const controller = new AbortController();
    fetchAssignments(controller.signal);
    refreshPendingInvites();

    // QUIET (2026-11-03): a role-change refresh must not flip `loading` —
    // BuyerRoute renders null while loading, so every page that announced a
    // change (adding a member, editing a role) was UNMOUNTED and remounted,
    // closing what the user had open (owner: "refresh the page rather than
    // adding them and allowing me to add more people"). First load stays loud.
    const handleRoleChange = () => {
      fetchAssignments(controller.signal, { quiet: true });
      refreshPendingInvites();
    };
    window.addEventListener('sfx-role-change', handleRoleChange);
    return () => {
      controller.abort();
      window.removeEventListener('sfx-role-change', handleRoleChange);
    };
  }, [fetchAssignments, refreshPendingInvites]);

  /**
   * Make `departmentId` active. THE single writer of the active department.
   *
   * 🔑 IT RECORDS THE SESSION ACK ITSELF RATHER THAN LEAVING IT TO THE CALLER,
   * and that is the *"every WRITER of a narrowed control's state must be
   * narrowed too"* rule applied here. There are THREE callers — the switcher,
   * `Layout.js`'s notification jump and `MyTeamTasks.js`'s gag jump — and if
   * only the switcher wrote the ack, following a notification into a personal
   * space would put you there and then evict you on the next load, which is
   * the exact incoherence the ack exists to remove.
   *
   * ⚠️ THE WARNING IS NOT RAISED HERE, DELIBERATELY, AND THE ASYMMETRY IS A
   * JUDGEMENT CALL WORTH KNOWING ABOUT. The owner asked for a warning when the
   * user *"chooses Myspace"* — that is the SWITCHER, a deliberate change of
   * where you are working. The other two callers are following a link to a
   * specific piece of content that happens to live in a personal space; the
   * user asked for the thing, not for the department, and interrupting that
   * with a modal about leaving their department would be noise in front of
   * something they already chose. So: those jumps do NOT warn, but they DO
   * hold, which is why the ack is written unconditionally here.
   */
  /**
   * ═════════════════════════════════════════════════════════════════════════
   * THE DIRTY-EDIT SWITCH GUARD (2026-08-30) — owner ruling, verbatim:
   * *"'You have unsaved changes — they'll be lost. Continue / Cancel.' is a
   * good idea, lets have the warning"*.
   * ═════════════════════════════════════════════════════════════════════════
   *
   * A REF, deliberately NOT `useState`. The switcher reads this SYNCHRONOUSLY
   * inside a click handler, before `switchDepartment` is ever called, so the
   * read must never lag a render — a `useState` value can be one render behind
   * the truth (an edit that just went dirty) in exactly the window this exists
   * to protect.
   *
   * `registerDirtyEditGuard(isDirty)` is called by whichever order page is
   * currently mounted — `DepartmentOrders.js` / `Approvals.js` / `MyOrders.js`
   * — in an effect keyed on their own `editDirtyId`, with the cleanup
   * unregistering (`false`) on unmount. Only one of the three is ever mounted
   * at a time (they are separate routes), so a single shared ref is sufficient
   * — no registry, no ids.
   *
   * `hasDirtyEdit()` is read by `ProductionDepartmentSwitcher.js` before
   * calling `switchDepartment`; a `true` result raises a Continue/Cancel
   * dialog instead of switching immediately. **Cancel must be a true no-op**:
   * neither this ref, `activeDepartment`, nor `localStorage.sfx_active_department_id`
   * are touched unless the user chooses Continue.
   *
   * ⚠️ **Deliberately NOT wired into `Layout.js`'s notification jump or
   * `MyTeamTasks.js`'s gag jump — the same two callers `switchDepartment`'s own
   * docblock already treats specially for the My Space warning, for the same
   * reason.** Both are "follow this content" jumps that can fire from any
   * page, not a considered "I am changing where I work" action; matching the
   * mySpace precedent above, they hold this ref's value irrelevant and switch
   * unconditionally. This is a real gap for data loss (unlike the informational
   * mySpace case) and is flagged, not silently accepted — a user with a dirty
   * order edit who clicks an unrelated notification can still lose it. Closing
   * it needs a dialog at each of those two call sites too; out of scope here.
   */
  const dirtyEditGuardRef = useRef(false);
  const registerDirtyEditGuard = useCallback((isDirty) => {
    dirtyEditGuardRef.current = isDirty;
  }, []);
  const hasDirtyEdit = useCallback(() => dirtyEditGuardRef.current, []);

  const switchDepartment = useCallback((departmentId) => {
    // 🔑 ONE `setActiveDepartment` CALL, and its literal COUNT in this file
    // is pinned at 4 by `activeDepartmentKeys.manifest.test.js` §3.1, which
    // also requires every argument to be `null` or a real assignment row.
    // `dept` is resolved first and set once, which keeps both properties
    // true. (The "All Productions" branch that used to sit here, resolving
    // to a synthetic sentinel row carrying only a `department_id`, was
    // removed on 2026-08-31 with the rest of the mode — an unrecognised
    // `departmentId` now simply finds nothing and returns.)
    const dept = assignments.find(d => d.department_id === departmentId);
    if (!dept) return;

    // 🆕 A SWITCH THAT DOES NOT ACTUALLY CHANGE SCOPE TOASTS NOTHING — read
    // BEFORE `setActiveDepartment` overwrites it. Compared by
    // `department_id`, not object identity: `dept` is freshly read from
    // `assignments` on every call, so re-selecting the department already
    // active is a real "same id, different object" case, not merely a
    // no-op call.
    const changed = activeDepartment?.department_id !== dept.department_id;

    setActiveDepartment(dept);
    safeWrite(localStorage, STORAGE_KEY, String(departmentId));
    if (changed) rememberLastDepartment(dept);

    if (isRealAssignment(dept)) {
      // Back somewhere real — the acceptance is spent. Without
      // this the ack outlives the choice it recorded, and the next reload
      // would drag the user back into My Space from somewhere they had
      // switched away from.
      safeRemove(sessionStorage, SESSION_PERSONAL_ACK_KEY);
    } else {
      safeWrite(sessionStorage, SESSION_PERSONAL_ACK_KEY, String(departmentId));
    }

    // 🔑 THE TOAST BELONGS HERE, NOT IN ANY CALLER — it must fire for all
    // FOUR `switchDepartment` callers (the switcher's two sites, `Layout.js`'s
    // notification click, `MyTeamTasks.js`'s `openGag`), and the last two
    // navigate somewhere specific rather than landing on a dashboard, so this
    // toast is the only signal those callers give that scope changed. Fired
    // after the switch is committed above, and only when it actually changed.
    if (changed) {
      const label = `${dept.production_name || 'Unknown'} — ${dept.department_name || 'Unknown'}`;
      toast.info(`Switched to ${label}`);
    }
  }, [assignments, activeDepartment, toast]);

  // Resolve the full active production (includes include_vat) whenever the
  // active department changes. The active department only carries production_id;
  // we fetch the row so VAT mode and other production-level settings can be
  // read site-wide via useVatMode() and related hooks.
  useEffect(() => {
    const productionId = activeDepartment?.production_id;
    if (!productionId) {
      setActiveProduction(null);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(`/api/productions/${productionId}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        setActiveProduction(data);
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Non-fatal. ⚠️ CORRECTED 2026-08-22 — this said *"useVatMode will
        // default to Ex VAT if activeProduction is null"*, and that has been
        // false since VAT display was removed on 2026-08-08: `hooks/useVatMode.js`
        // calls `useDepartment()` only to keep its identity as a hook and
        // deliberately does NOT read `activeProduction` — `VAT_DISPLAY_ENABLED`
        // is a hard `false`, so no money figure moves either way. The comment
        // cost a survey real time on a false money lead; it is left here as a
        // pointer rather than deleted so the next reader does not re-derive it.
        //
        // What a null `activeProduction` DOES affect is every other
        // production-level setting read through the context — none of them
        // money — and this failure is still silent. That is a smaller sibling
        // of the `assignmentsError` defect above and is deliberately NOT fixed
        // in this change; no surface currently makes a confident claim from it.
      }
    })();
    return () => controller.abort();
  }, [activeDepartment?.production_id]);

  // Derive activeRole from activeDepartment
  const activeRole = activeDepartment ? activeDepartment.role : null;

  // Per-department capabilities for the department the user is STANDING IN.
  //
  // Derived from `activeDepartment` — the same state the dropdown writes via
  // switchDepartment — so it re-derives on every department switch rather than
  // being captured once. Never hoist this into its own useState: a capability
  // set that outlives a department switch is an any-department flag wearing a
  // department's name (CLAUDE.md, "Authority model").
  //
  // `{}` when there is no active department (an admin, or a user with no
  // assignments), so callers can read `capabilities.canX` without guarding.
  // Absent means false — this is a UI convenience, and the server gate is what
  // actually enforces any of it.
  //
  // 📍 PROVENANCE, recorded because a task brief got this wrong on 2026-08-06
  // and the next one will too. `capabilities` and the server half that fills it
  // (`server/routes/roles.js`, the `row.capabilities = await
  // departmentAuthority(req, row.department_id)` loop in GET
  // /api/roles/my-assignments) BOTH SHIPPED WITH PART B. They are not
  // outstanding work. What was outstanding until this commit is (a) any test of
  // either — `client/src` has no static scan and nothing here failed CI, so the
  // "switching department switches the answer" property was carried by this
  // derivation and a comment — and (b) the ~100 gates that still read the
  // unscoped `isBuyer` while this sits next to them. See
  // `client/src/clientAuthorityManifest.test.js`.
  const capabilities = activeDepartment?.capabilities || {};

  // 🔀 PER-SEAT STORES (owner rulings 1 + 20, 2026-09-23). Was a useState filled
  // once from the payload's top-level flag — a per-PERSON grant that also
  // OUTLIVED a department switch, the exact rewrite this derivation forbids.
  // Now the active department's SEAT answer, so the Deliveries nav item, the
  // /department/orders route guard and isStoresOnly follow the switcher. An
  // admin has no rows (`{}`) and is carried by isAdmin. Order-scoped surfaces
  // must use capabilitiesFor(order.department_id).canManageStores instead.
  // Pinned by DepartmentContext.capabilities.test.js §6.
  const canManageStores = capabilities.canManageStores === true;

  // Cost code / payment type / budget pot are sensitive accounting fields —
  // viewable only by buyer / admin / supervisor (mirrors the server read gate
  // in routes/orders.js). One source of truth so every display site agrees.
  //
  // 🚨 DEPARTMENT-SCOPED SINCE 2026-08-17 (owner ruling — a department-scoped
  // buyer/supervisor must see Cost Code + Payment Type on every order page,
  // including the cost-code editor on To Approve). Used to read the UNSCOPED
  // `isBuyer` (`isAdmin || isBuyer || activeRole === 'supervisor'`), which
  // wrongly refused a correctly-created department buyer with no unscoped
  // `user_roles` row — the tranche-3 end-state for EVERY buyer. The server
  // already resolves this per order via `departmentAuthority(req,
  // deptId).canViewOrderAccounting` (`routes/orders.js`); this now asks the
  // SAME question, using `capabilities` above (the ACTIVE department's
  // resolved capability set, populated by GET /api/roles/my-assignments)
  // rather than a once-per-request flag. `isAdmin ||` stays: an admin holds
  // no assignment rows, so `capabilities` is `{}` for them — the documented
  // pattern above. Consequence: an unscoped buyer standing in a department
  // where they hold no qualifying role now correctly stops seeing these
  // fields client-side — the server already stripped them; the client was
  // just too permissive.
  const canViewAccounting = isAdmin || Boolean(capabilities.canViewOrderAccounting);

  /**
   * The capability set for ANY department the caller is assigned to — not just
   * the one they are standing in.
   *
   * WHY THIS EXISTS ALONGSIDE `capabilities`. The server resolves order
   * authority in THE ORDER'S OWN department
   * (`requireDepartmentCapability(req, res, order.department_id, …)`), and an
   * order surface can list orders from a department the caller is not standing
   * in: `GET /api/orders?user_id=` returns the caller's own orders across every
   * department (qualified below). Gating that on the ACTIVE department's
   * capabilities would answer a different question from the server and would be
   * wrong in both directions.
   *
   * 🔻 **THIS SAID "two of the three order surfaces" AND NAMED
   * `GET /api/approvals/pending` AS "not department-scoped at all (it selects on
   * `approver_user_id`)". THAT EXPIRED ON 2026-08-12** — product owner: *"only
   * site admin sees more than one department at a time i think"* — and the
   * endpoint now carries `server/helpers/pendingApprovalsScope.js`'s filter.
   * **The conclusion is untouched and must not be "tidied" now that one of its
   * two examples has gone.** `capabilitiesFor(order.department_id)` is right
   * because it asks the server's question, not because the list happened to span
   * departments; and a SITE ADMIN's approvals list still does span them.
   *
   * ⚠️ THE `GET /api/orders?user_id=` HALF IS TRUE ONLY WHEN NO `x-department-id`
   * HEADER IS SENT — corrected 2026-08-10, having been quoted unqualified into a
   * task brief. 🔻 **AND THAT QUALIFICATION ITSELF EXPIRED ON 2026-08-30**
   * (owner: *"yes gate my orders to the selected department, and to approve"*):
   * `routes/orders.js` now narrows that path for every non-admin WITHOUT
   * reading the header at all — the middleware defaults an active department
   * from the caller's own assignments when the header is absent, and a caller
   * with none fails closed to their own NULL-department rows. So a non-admin's
   * MyOrders list is single-department-plus-own-orphans in EVERY header state,
   * not merely "essentially every real request". This docblock's third
   * supporting example to expire while the rule stood (see below) — the
   * CONCLUSION is again unchanged: `capabilitiesFor(order.department_id)` is
   * still the right question, because a SITE ADMIN's list still spans
   * departments and a NULL-department order is never covered by the active
   * department's capabilities either way.
   *
   * 🔻 THIS SENTENCE ENDED *"`GET /api/approvals/pending` is unqualified and
   * genuinely spans departments"* UNTIL 2026-08-12, WHEN THAT ENDPOINT WAS
   * SCOPED. 🕶️ **Note what the two corrections have in common, because it is the
   * useful part: this docblock has now had BOTH of its supporting examples
   * expire, eighteen months of "obviously true" apart, while the rule it teaches
   * never moved.** An example is the first thing to rot and the last thing a
   * reader checks. For a non-admin the approvals list is now single-department;
   * for a site admin it still spans.
   *
   * Assignment rows carry `capabilities` because GET /api/roles/my-assignments
   * attaches one per row, so this is a lookup, not a second resolution path.
   * There is no client-side derivation of authority here and there must never
   * be one: this reports an answer the server already computed.
   *
   * A department the caller holds no assignment in returns NO_CAPABILITIES,
   * which is what `departmentAuthority` returns for the same case.
   *
   * 🪤 `Number(null)` is `0`, not `NaN`, so `Number.isInteger` alone would
   * accept a null department id and then match an assignment row only by
   * accident. The `> 0` is load-bearing — see CLAUDE.md on the same trap in
   * `Number(null)` making orphaned rows fail closed.
   */
  /**
   * Does this caller hold department-settings rights in ANY department they are
   * assigned to — not just the one they are standing in?
   *
   * 🔑 THIS DECIDES WHETHER A LINK IS DRAWN AND A DOOR OPENS, AND NOTHING ELSE.
   * IT IS NOT AN ANY-DEPARTMENT AUTHORITY FLAG, and it must never become one.
   * Every request behind the door it opens is still resolved PER DEPARTMENT by
   * the server, and every control inside renders from a server-computed answer
   * for the specific row it acts on.
   *
   * ⚠️ IT HAS THE SHAPE OF THE CARRY-OVER DEFECT TRANCHE 3 EXISTS TO DELETE, so
   * it will look wrong to the next reader and someone will try to "fix" it back
   * to `capabilities.canManageDepartmentSettings`. Do not. That narrower test is
   * what caused the 2026-08-10 lockout: `capabilities` is the ACTIVE
   * department's set, so a supervisor who runs department A but happens to be
   * standing in department B — or in their own My Space — was refused the page
   * that administers A, while the server admitted every request they would have
   * made from it. The sealed box is about what you may DO in a department; it
   * was never about which screens exist.
   *
   * It is deliberately built from `assignments`, which already carry the
   * server's per-department capability sets, so this is a lookup and not a
   * second derivation of authority.
   */
  const holdsDepartmentSettingsSomewhere = assignments.some(
    (a) => a.capabilities?.canManageDepartmentSettings === true
  );

  const capabilitiesFor = useCallback((departmentId) => {
    const id = Number(departmentId);
    if (!Number.isInteger(id) || id <= 0) return NO_CAPABILITIES;
    const match = assignments.find((a) => Number(a.department_id) === id);
    return match?.capabilities || NO_CAPABILITIES;
  }, [assignments]);

  const value = {
    assignments,
    activeDepartment,
    activeProduction,
    activeRole,
    switchDepartment,
    registerDirtyEditGuard,
    hasDirtyEdit,
    isAdmin,
    isBuyer,
    canViewBudget,
    canManageBudget,
    canManageStores,
    canViewAccounting,
    capabilities,
    capabilitiesFor,
    holdsDepartmentSettingsSomewhere,
    ownsProductionLabel,
    // See the state declaration: TRUE means the assignment request failed, so
    // `assignments`/`activeDepartment`/the capability flags below are unknown
    // rather than empty. Never read it as authority.
    assignmentsError,
    // Pending department invites — NOT seats; never read them for authority
    // and never merge them into `assignments` (see the state declaration).
    pendingInvites,
    pendingInvitesLoaded,
    refreshPendingInvites,
    acceptInvite,
    loading: loading || !resolved
  };

  return (
    <DepartmentContext.Provider value={value}>
      {children}
    </DepartmentContext.Provider>
  );
}

export function useDepartment() {
  const context = useContext(DepartmentContext);
  if (!context) {
    throw new Error('useDepartment must be used within a DepartmentProvider');
  }
  return context;
}

export default DepartmentContext;
